import { useCallback, useSyncExternalStore } from 'react'
import type { ExtendedLocalStorageKey, ExtendedLocalStorageValueMap } from '@core/app/types'
import { setLocalStorageItem, removeLocalStorageItem } from '@core/utils/helpers/localStorageHelpers'

const snapshotCache = new Map<string, () => any>()

function subscribe(callback: () => void): () => void {
    const handler = () => callback()
    window.addEventListener('localStorageChange', handler)
    window.addEventListener('storage', handler)
    return () => {
        window.removeEventListener('localStorageChange', handler)
        window.removeEventListener('storage', handler)
    }
}

function getOrCreateSnapshot<K extends ExtendedLocalStorageKey>(
    key: K,
    defaultValue?: ExtendedLocalStorageValueMap[K]
): () => ExtendedLocalStorageValueMap[K] | null {
    if (snapshotCache.has(key)) {
        return snapshotCache.get(key) as () => ExtendedLocalStorageValueMap[K] | null
    }

    let lastRaw: string | null = null
    let lastParsed: ExtendedLocalStorageValueMap[K] | null = null

    const snapshotFn = () => {
        const raw = localStorage.getItem(key)
        if (raw === lastRaw) return lastParsed

        lastRaw = raw
        try {
            lastParsed = raw ? (JSON.parse(raw) as ExtendedLocalStorageValueMap[K]) : (defaultValue ?? null)
        } catch {
            lastParsed = defaultValue ?? null
        }
        return lastParsed
    }

    snapshotCache.set(key, snapshotFn)
    return snapshotFn
}

function useLocalStorageStore<K extends ExtendedLocalStorageKey>(
    key: K,
    defaultValue?: ExtendedLocalStorageValueMap[K]
): [ExtendedLocalStorageValueMap[K] | null, (value: ExtendedLocalStorageValueMap[K]) => void, () => void] {
    const value = useSyncExternalStore(subscribe, getOrCreateSnapshot(key, defaultValue))

    const setValue = useCallback(
        (val: ExtendedLocalStorageValueMap[K]) => {
            setLocalStorageItem(key, val)
            window.dispatchEvent(new CustomEvent('localStorageChange'))
        },
        [key]
    )

    const removeValue = useCallback(() => {
        removeLocalStorageItem(key)
        window.dispatchEvent(new CustomEvent('localStorageChange'))
    }, [key])

    return [value, setValue, removeValue]
}

export default useLocalStorageStore
